import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { adminApi, type AgentRoleMaster, type AgentTypeWithRoles } from '../api/admin';

// マスタ管理（#176）: エージェント区分(type) / 責務(role)。
// 責務は区分に従属（区分カードの中に責務を並べる＝「開発系に開発系でない責務」を入れさせない構造）。
const MASTERS_KEY = ['agent-masters'];

export function MasterManagementPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: MASTERS_KEY,
    queryFn: () => adminApi.listAgentMasters(),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: MASTERS_KEY });

  const [newTypeOpen, setNewTypeOpen] = useState(false);

  const types = data?.types ?? [];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h2 style={{ margin: 0 }}>マスタ管理</h2>
          <p className="k-muted" style={{ margin: '4px 0 0', fontSize: 13.5 }}>
            エージェントの<b>区分</b>（開発系 / 会社運用系…）と<b>責務</b>を管理します。
            責務は区分に従属します（区分をまたいだ責務は作れません）。
          </p>
        </div>
        <button type="button" onClick={() => setNewTypeOpen(true)}>
          ＋ 区分を追加
        </button>
      </div>

      {newTypeOpen && (
        <TypeForm
          onClose={() => setNewTypeOpen(false)}
          onSubmit={async (v) => {
            await adminApi.createAgentType(v);
            await invalidate();
            setNewTypeOpen(false);
          }}
        />
      )}

      {isLoading && <p className="k-muted" style={{ marginTop: 16 }}>読み込み中…</p>}

      <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
        {types.map((t) => (
          <TypeCard key={t.id} type={t} onChanged={invalidate} />
        ))}
        {!isLoading && types.length === 0 && (
          <div className="k-card">
            <p className="k-muted" style={{ margin: 0 }}>
              区分がまだありません。右上の「＋ 区分を追加」から作成してください。
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── 区分カード（中に従属する責務一覧） ──
function TypeCard({ type, onChanged }: { type: AgentTypeWithRoles; onChanged: () => void }) {
  const [editOpen, setEditOpen] = useState(false);
  const [addRoleOpen, setAddRoleOpen] = useState(false);

  const del = useMutation({
    mutationFn: () => adminApi.deleteAgentType(type.id),
    onSuccess: onChanged,
  });

  return (
    <div className="k-card">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>{type.label}</h3>
          <code style={{ fontSize: 12, color: '#64748b' }}>{type.code}</code>
          {!type.enabled && <span style={{ fontSize: 12, color: '#b45309' }}>（無効）</span>}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="btn-ghost" onClick={() => setAddRoleOpen(true)}>
            ＋ 責務を追加
          </button>
          <button type="button" className="btn-ghost" onClick={() => setEditOpen(true)}>
            区分を編集
          </button>
          <button
            type="button"
            className="btn-danger"
            onClick={() => {
              if (confirm(`区分「${type.label}」を削除すると配下の責務も全て削除されます。よろしいですか？`))
                del.mutate();
            }}
          >
            削除
          </button>
        </div>
      </div>

      {editOpen && (
        <TypeForm
          initial={type}
          onClose={() => setEditOpen(false)}
          onSubmit={async (v) => {
            await adminApi.updateAgentType(type.id, { label: v.label, sort: v.sort, enabled: v.enabled });
            onChanged();
            setEditOpen(false);
          }}
        />
      )}
      {addRoleOpen && (
        <RoleForm
          typeLabel={type.label}
          onClose={() => setAddRoleOpen(false)}
          onSubmit={async (v) => {
            await adminApi.createAgentRole({ typeId: type.id, ...v });
            onChanged();
            setAddRoleOpen(false);
          }}
        />
      )}

      <table className="k-table" style={{ marginTop: 12, width: '100%' }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', width: 60 }}>順</th>
            <th style={{ textAlign: 'left' }}>責務（表示名）</th>
            <th style={{ textAlign: 'left' }}>コード</th>
            <th style={{ textAlign: 'left', width: 80 }}>状態</th>
            <th style={{ width: 140 }}></th>
          </tr>
        </thead>
        <tbody>
          {type.roles.map((r) => (
            <RoleRow key={r.id} role={r} onChanged={onChanged} />
          ))}
          {type.roles.length === 0 && (
            <tr>
              <td colSpan={5} className="k-muted" style={{ padding: '10px 4px' }}>
                この区分の責務がまだありません。「＋ 責務を追加」から登録してください。
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function RoleRow({ role, onChanged }: { role: AgentRoleMaster; onChanged: () => void }) {
  const [editOpen, setEditOpen] = useState(false);
  const del = useMutation({
    mutationFn: () => adminApi.deleteAgentRole(role.id),
    onSuccess: onChanged,
  });
  return (
    <>
      <tr>
        <td>{role.sort}</td>
        <td>{role.label}</td>
        <td>
          <code style={{ fontSize: 12, color: '#64748b' }}>{role.code}</code>
        </td>
        <td>{role.enabled ? '有効' : <span style={{ color: '#b45309' }}>無効</span>}</td>
        <td style={{ textAlign: 'right' }}>
          <button type="button" className="btn-ghost" onClick={() => setEditOpen(true)}>
            編集
          </button>{' '}
          <button
            type="button"
            className="btn-danger"
            onClick={() => {
              if (confirm(`責務「${role.label}」を削除しますか？`)) del.mutate();
            }}
          >
            削除
          </button>
        </td>
      </tr>
      {editOpen && (
        <tr>
          <td colSpan={5}>
            <RoleForm
              initial={role}
              onClose={() => setEditOpen(false)}
              onSubmit={async (v) => {
                await adminApi.updateAgentRole(role.id, v);
                onChanged();
                setEditOpen(false);
              }}
            />
          </td>
        </tr>
      )}
    </>
  );
}

// ── フォーム（区分） ──
function TypeForm({
  initial,
  onClose,
  onSubmit,
}: {
  initial?: { code: string; label: string; sort: number; enabled: boolean };
  onClose: () => void;
  onSubmit: (v: { code: string; label: string; sort: number; enabled: boolean }) => Promise<void>;
}) {
  const [code, setCode] = useState(initial?.code ?? '');
  const [label, setLabel] = useState(initial?.label ?? '');
  const [sort, setSort] = useState(initial?.sort ?? 0);
  const [enabled, setEnabled] = useState(initial?.enabled ?? true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const editing = !!initial;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await onSubmit({ code: code.trim(), label: label.trim(), sort: Number(sort) || 0, enabled });
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : '保存に失敗しました');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} style={formBox}>
      <strong>{editing ? '区分を編集' : '区分を追加'}</strong>
      <div style={fieldRow}>
        <input
          placeholder="コード（例: dev）"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          disabled={editing}
          style={{ width: 180 }}
        />
        <input placeholder="表示名（例: 開発系）" value={label} onChange={(e) => setLabel(e.target.value)} />
        <input
          type="number"
          placeholder="順"
          value={sort}
          onChange={(e) => setSort(Number(e.target.value))}
          style={{ width: 80 }}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} /> 有効
        </label>
      </div>
      {editing && <p className="k-muted" style={{ margin: 0, fontSize: 12 }}>コードは変更できません（エージェントが参照するため）。</p>}
      {err && <p style={{ color: '#dc2626', margin: 0, fontSize: 13 }}>{err}</p>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="submit" disabled={busy || !code.trim() || !label.trim()}>
          保存
        </button>
        <button type="button" className="btn-ghost" onClick={onClose}>
          キャンセル
        </button>
      </div>
    </form>
  );
}

// ── フォーム（責務）。typeId は親区分に固定（従属） ──
function RoleForm({
  typeLabel,
  initial,
  onClose,
  onSubmit,
}: {
  typeLabel?: string;
  initial?: { code: string; label: string; sort: number; enabled: boolean };
  onClose: () => void;
  onSubmit: (v: { code: string; label: string; sort: number; enabled: boolean }) => Promise<void>;
}) {
  const [code, setCode] = useState(initial?.code ?? '');
  const [label, setLabel] = useState(initial?.label ?? '');
  const [sort, setSort] = useState(initial?.sort ?? 0);
  const [enabled, setEnabled] = useState(initial?.enabled ?? true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const editing = !!initial;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await onSubmit({ code: code.trim(), label: label.trim(), sort: Number(sort) || 0, enabled });
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : '保存に失敗しました');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} style={formBox}>
      <strong>
        {editing ? '責務を編集' : '責務を追加'}
        {typeLabel && <span className="k-muted" style={{ fontWeight: 400 }}>（区分: {typeLabel}）</span>}
      </strong>
      <div style={fieldRow}>
        <input
          placeholder="コード（例: deployer）"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          style={{ width: 200 }}
        />
        <input placeholder="表示名（例: デプロイヤー）" value={label} onChange={(e) => setLabel(e.target.value)} />
        <input
          type="number"
          placeholder="順"
          value={sort}
          onChange={(e) => setSort(Number(e.target.value))}
          style={{ width: 80 }}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} /> 有効
        </label>
      </div>
      {err && <p style={{ color: '#dc2626', margin: 0, fontSize: 13 }}>{err}</p>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="submit" disabled={busy || !code.trim() || !label.trim()}>
          保存
        </button>
        <button type="button" className="btn-ghost" onClick={onClose}>
          キャンセル
        </button>
      </div>
    </form>
  );
}

const formBox: React.CSSProperties = {
  marginTop: 12,
  padding: 14,
  border: '1px solid #dbe3ee',
  borderRadius: 10,
  background: '#f8fafc',
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
};
const fieldRow: React.CSSProperties = { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' };
